import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { statsApi } from '../services/statsApi';
import './AdminDashboard.css';

// Simple line chart component
function LineChart({ data, period }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 640);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  if (!data || data.length === 0) {
    return <div className="admin-chart-empty">No data available</div>;
  }

  // Calculate max visible ticks based on screen size
  const maxTicks = isSmallScreen ? 4 : 6;
  const tickInterval = Math.max(1, Math.floor(data.length / maxTicks));
  const visibleIndices = [];
  for (let i = 0; i < data.length; i += tickInterval) {
    visibleIndices.push(i);
  }
  // Always show last point if not already included
  if (visibleIndices[visibleIndices.length - 1] !== data.length - 1) {
    visibleIndices.push(data.length - 1);
  }

  const maxCount = Math.max(...data.map(d => d.count), 1);
  const height = 200;
  const width = 100;
  const points = data.map((point, index) => {
    const x = (index / (data.length - 1)) * width;
    const y = height - (point.count / maxCount) * height;
    return { x, y, ...point };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  // Format timestamp based on period
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    if (period === 'minute') {
      // 24 Hours: show time only (HH:mm)
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (period === 'hour') {
      // 7 Days: show day + time (MMM D)
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
    // 30 Days: show date only (MMM D)
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Format full timestamp for tooltip
  const formatFullTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Determine if rotation is needed based on data density
  const needsRotation = data.length > 8;

  return (
    <div className="admin-line-chart">
      <svg viewBox={`0 0 ${width} ${height + 30}`} preserveAspectRatio="none">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
          <line
            key={i}
            x1="0"
            y1={height * ratio}
            x2={width}
            y2={height * ratio}
            stroke="#e2e8f0"
            strokeWidth="0.5"
          />
        ))}

        {/* Area fill */}
        <defs>
          <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <path
          d={`${pathD} L ${width} ${height} L 0 ${height} Z`}
          fill="url(#chartGradient)"
        />

        {/* Line */}
        <path
          d={pathD}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Hover point */}
        {hoveredIndex !== null && points[hoveredIndex] && (
          <circle
            cx={points[hoveredIndex].x}
            cy={points[hoveredIndex].y}
            r="4"
            fill="#3b82f6"
            stroke="#ffffff"
            strokeWidth="2"
          />
        )}
      </svg>

      {/* X-axis labels */}
      <div className={`admin-chart-labels ${needsRotation ? 'rotated' : ''}`}>
        {visibleIndices.map((index) => (
          <span
            key={index}
            className={`admin-chart-label ${index === hoveredIndex ? 'active' : ''}`}
            style={{ left: `${(index / (data.length - 1)) * 100}%` }}
          >
            {formatTime(data[index].timestamp)}
          </span>
        ))}
      </div>

      {/* Tooltip with full timestamp */}
      {hoveredIndex !== null && data[hoveredIndex] && (
        <div className="admin-chart-tooltip">
          <strong>{data[hoveredIndex].count.toLocaleString()}</strong> visits
          <br />
          <span>{formatFullTime(data[hoveredIndex].timestamp)}</span>
        </div>
      )}

      {/* Hover area */}
      <div className="admin-chart-hover-area">
        {data.map((point, index) => (
          <div
            key={index}
            className="admin-chart-hover-point"
            style={{ left: `${(index / (data.length - 1)) * 100}%` }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          />
        ))}
      </div>
    </div>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [visitHistory, setVisitHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('hour');

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadVisitHistory();
  }, [period]);

  const loadStats = async () => {
    try {
      const response = await statsApi.getDashboard();
      if (response.success) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadVisitHistory = async () => {
    try {
      const response = await statsApi.getVisitHistory(period, 24);
      if (response.success) {
        setVisitHistory(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load visit history:', err);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard-loading">
        <div className="admin-spinner"></div>
        <span>Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Platform Activity */}
      <section className="admin-section">
        <h2 className="admin-section-title">Platform Activity</h2>
        <div className="admin-stats-grid">
          <div className="admin-stat-card" onClick={() => navigate('/admin/users')} style={{ cursor: 'pointer' }}>
            <div className="admin-stat-icon visits">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-label">Visits Today</span>
              <span className="admin-stat-value">{stats?.visits_day?.toLocaleString() || 0}</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="admin-stat-icon week">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-label">Visits This Week</span>
              <span className="admin-stat-value">{stats?.visits_week?.toLocaleString() || 0}</span>
            </div>
          </div>

          <div className="admin-stat-card" onClick={() => navigate('/admin/users')} style={{ cursor: 'pointer' }}>
            <div className="admin-stat-icon users">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-label">Active Users Today</span>
              <span className="admin-stat-value">{stats?.active_users?.toLocaleString() || 0}</span>
            </div>
          </div>

          <div className="admin-stat-card" onClick={() => navigate('/admin/users')} style={{ cursor: 'pointer' }}>
            <div className="admin-stat-icon total">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </svg>
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-label">Total Users</span>
              <span className="admin-stat-value">{stats?.total_users?.toLocaleString() || 0}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content Metrics */}
      <section className="admin-section">
        <h2 className="admin-section-title">Content Metrics</h2>
        <div className="admin-stats-grid admin-stats-grid-5">
          <div className="admin-stat-card">
            <div className="admin-stat-icon posts">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-label">Total Posts</span>
              <span className="admin-stat-value">{stats?.total_posts?.toLocaleString() || 0}</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="admin-stat-icon messages">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-label">Total Messages</span>
              <span className="admin-stat-value">{stats?.total_messages?.toLocaleString() || 0}</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="admin-stat-icon images">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21,15 16,10 5,21" />
              </svg>
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-label">Total Images</span>
              <span className="admin-stat-value">{stats?.total_images?.toLocaleString() || 0}</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="admin-stat-icon videos">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="23,7 16,12 23,17 23,7" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-label">Total Videos</span>
              <span className="admin-stat-value">{stats?.total_videos?.toLocaleString() || 0}</span>
            </div>
          </div>

          <div className="admin-stat-card">
            <div className="admin-stat-icon audios">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18V5l12-2v13" />
                <circle cx="6" cy="18" r="3" />
                <circle cx="18" cy="16" r="3" />
              </svg>
            </div>
            <div className="admin-stat-content">
              <span className="admin-stat-label">Total Audios</span>
              <span className="admin-stat-value">{stats?.total_audios?.toLocaleString() || 0}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Visit Chart */}
      <section className="admin-section">
        <div className="admin-section-header">
          <h2 className="admin-section-title">Visit History</h2>
          <div className="admin-period-selector">
            <button
              className={`admin-period-btn ${period === 'minute' ? 'active' : ''}`}
              onClick={() => setPeriod('minute')}
            >
              24 Hours
            </button>
            <button
              className={`admin-period-btn ${period === 'hour' ? 'active' : ''}`}
              onClick={() => setPeriod('hour')}
            >
              7 Days
            </button>
            <button
              className={`admin-period-btn ${period === 'day' ? 'active' : ''}`}
              onClick={() => setPeriod('day')}
            >
              30 Days
            </button>
          </div>
        </div>
        <div className="admin-chart-container">
          <LineChart data={visitHistory} period={period} />
        </div>
      </section>
    </div>
  );
}

export default AdminDashboard;